import { useTranslations } from '@aikoa/i18n/react'
import { useState } from 'react'
import { View } from 'react-native'
import z from 'zod'
import { useAlert } from '~/components/GlobalAlert'
import { Body, Label } from '~/components/typography/Content'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { useCreateUserInvitation } from '~/data/invitations/writeInvitations'
import { cn } from '~/lib/utils'

const inviteEmailSchema = z.email()

type InviteTeamMemberFormProps = {
  className?: string
}

export function InviteTeamMemberForm({ className }: InviteTeamMemberFormProps) {
  const { t } = useTranslations(['home', 'common'])
  const { showAlert } = useAlert()

  const createUserInvitationMutation = useCreateUserInvitation()

  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true)

  const handleSendInvitation = () => {
    const normalizedEmail = email.trim().toLowerCase()

    if (!inviteEmailSchema.safeParse(normalizedEmail).success) {
      showAlert({
        type: 'error',
        title: t(($) => $.common.error.error),
        description: t(($) => $.realtor.inviteTeam.errors.invalidEmail),
      })
      return
    }

    if (phoneNumber && !isPhoneNumberValid) {
      showAlert({
        type: 'error',
        title: t(($) => $.common.error.error),
        description: t(($) => $.realtor.inviteTeam.errors.invalidPhone),
      })
      return
    }

    createUserInvitationMutation.mutate(
      {
        email: normalizedEmail,
        phone: phoneNumber || undefined,
      },
      {
        onSuccess: () => {
          setEmail('')
          setPhoneNumber('')
          setIsPhoneNumberValid(true)

          showAlert({
            type: 'success',
            title: t(($) => $.realtor.inviteTeam.successTitle),
            description: t(($) => $.realtor.inviteTeam.successDescription),
          })
        },
        onError: () => {
          showAlert({
            type: 'error',
            title: t(($) => $.common.error.error),
            description: t(($) => $.realtor.inviteTeam.errors.sendFailed),
          })
        },
      }
    )
  }

  return (
    <View className={cn('gap-6', className)}>
      <View className="gap-2">
        <Label>{t(($) => $.realtor.inviteTeam.emailLabel)}</Label>
        <Input
          value={email}
          onChangeText={setEmail}
          placeholder={t(($) => $.realtor.inviteTeam.emailPlaceholder)}
          autoCapitalize="none"
          autoCorrect={false}
          keyboardType="email-address"
          variant="box"
        />
      </View>

      <View className="gap-2">
        <View className="flex-row items-center gap-2">
          <Label>{t(($) => $.realtor.inviteTeam.phoneLabel)}</Label>
          <Body className="text-sm text-secondary">{t(($) => $.realtor.inviteTeam.optional)}</Body>
        </View>

        <Input
          type="phone"
          value={phoneNumber}
          onChangeText={(_displayPhoneNumber, _countryCode, storedPhoneNumber, isValid) => {
            setPhoneNumber(storedPhoneNumber)
            setIsPhoneNumberValid(storedPhoneNumber ? isValid : true)
          }}
          placeholder={t(($) => $.realtor.inviteTeam.phonePlaceholder)}
          variant="box"
        />
      </View>

      <Button
        variant="primary"
        disabled={createUserInvitationMutation.isPending}
        loading={createUserInvitationMutation.isPending}
        onPress={handleSendInvitation}>
        <ButtonLabel>{t(($) => $.realtor.inviteTeam.sendButton)}</ButtonLabel>
      </Button>
    </View>
  )
}
